"use client";

interface QuotaProgressMiniWidgetProps {
  className?: string;
}

// Format currency in Spanish style (from the real codebase)
const formatCurrency = (amount: number) => {
  const formattedNumber = new Intl.NumberFormat("es-ES", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
    useGrouping: true,
  }).format(amount);
  return `${formattedNumber} \u20AC`;
};

// Mock data - realistic values for a film promoter
const mockQuotaData = {
  fiscalYear: 2025,
  totalAmount: 850000,
  completedAmount: 425000,
  reservedAmount: 212500,
  availableAmount: 212500,
};

export function QuotaProgressMiniWidget({ className }: QuotaProgressMiniWidgetProps) {
  const { totalAmount, completedAmount, reservedAmount, availableAmount } = mockQuotaData;

  // Calculate percentages (exactly as in the real app)
  const completedPercentage =
    totalAmount > 0 ? Math.round((completedAmount / totalAmount) * 100) : 0;
  const reservedPercentage = totalAmount > 0 ? Math.round((reservedAmount / totalAmount) * 100) : 0;
  const totalOccupiedPercentage = completedPercentage + reservedPercentage;

  return (
    <div className={className}>
      {/* Card Container - wider to prevent tooltip text wrapping */}
      <div className="w-72 bg-white rounded-lg border border-zinc-200 shadow-sm overflow-hidden">
        {/* Header */}
        <div className="px-4 py-3 border-b border-zinc-100">
          <div className="text-[11px] font-medium text-zinc-500 uppercase tracking-wider">
            Estado de Inversion
          </div>
        </div>

        {/* Content */}
        <div className="px-4 py-4">
          {/* Tooltip ABOVE progress bar - matching exact app styling */}
          <div className="relative mb-2">
            <div className="text-zinc-800 bg-white border border-zinc-200 rounded-md shadow-lg px-3 py-2 text-xs">
              {/* Fiscal year header */}
              <div className="font-semibold text-xs border-b border-zinc-200 pb-1.5 mb-2 text-center">
                Año Fiscal 2026
              </div>

              <div className="space-y-1.5">
                {/* Target/Objetivo row */}
                <div className="flex justify-between items-center text-[12px]">
                  <span className="text-zinc-600">Objetivo:</span>
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-zinc-900">{formatCurrency(totalAmount)}</span>
                    <span className="text-zinc-500 w-10 text-right">100%</span>
                  </div>
                </div>

                {/* Confirmed/Confirmado row */}
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-[1px] bg-quota-bar-completed flex-shrink-0" />
                  <div className="flex justify-between items-center text-[12px] flex-1">
                    <span className="text-zinc-600">Confirmado:</span>
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-zinc-900">
                        {formatCurrency(completedAmount)}
                      </span>
                      <span className="text-zinc-500 w-10 text-right">{completedPercentage}%</span>
                    </div>
                  </div>
                </div>

                {/* Reserved/Reservado row */}
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-[1px] bg-quota-bar-reserved flex-shrink-0" />
                  <div className="flex justify-between items-center text-[12px] flex-1">
                    <span className="text-zinc-600">Reservado:</span>
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-zinc-900">
                        {formatCurrency(reservedAmount)}
                      </span>
                      <span className="text-zinc-500 w-10 text-right">{reservedPercentage}%</span>
                    </div>
                  </div>
                </div>

                {/* Available/Disponible row - with separator */}
                <div className="flex items-center gap-2 pt-1.5 border-t border-zinc-200">
                  <div className="w-2 h-2 rounded-[1px] bg-quota-bar-available flex-shrink-0" />
                  <div className="flex justify-between items-center text-[12px] flex-1">
                    <span className="text-zinc-600">Disponible:</span>
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-zinc-900">
                        {formatCurrency(availableAmount)}
                      </span>
                      <span className="text-zinc-500 w-10 text-right">
                        {100 - totalOccupiedPercentage}%
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* Arrow/notch pointing DOWN to the progress bar */}
            <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-[1px] w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-t-[6px] border-t-white" />
            {/* Border for the arrow */}
            <div className="absolute top-full left-1/2 -translate-x-1/2 mt-[-2px] w-0 h-0 border-l-[7px] border-l-transparent border-r-[7px] border-r-transparent border-t-[7px] border-t-zinc-200 -z-10" />
          </div>

          {/* Progress Bar - exact style from the app, now BELOW tooltip */}
          <div className="flex items-center justify-center mt-3">
            <div className="w-16 bg-quota-bar-available rounded-full h-2 mr-2 relative overflow-hidden">
              {/* Completed segment */}
              <div
                className="bg-quota-bar-completed h-2 absolute left-0"
                style={{ width: `${completedPercentage}%` }}
              />
              {/* Reserved segment */}
              <div
                className="bg-quota-bar-reserved h-2 absolute"
                style={{
                  width: `${reservedPercentage}%`,
                  left: `${completedPercentage}%`,
                }}
              />
            </div>
            <span className="text-xs font-medium text-zinc-500">{totalOccupiedPercentage}%</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default QuotaProgressMiniWidget;
